/**
 * Whether this browser will actually hand out a WebGL context.
 *
 * Plenty of clients won't: software rasterizers (headless Chromium on a GPU-less
 * host), GPU blocklists, `webgl.disabled`, remote desktop sessions, hardened
 * browsers. three.js throws from the WebGLRenderer constructor when context
 * creation fails, and because @react-three/fiber configures the canvas from an
 * unawaited async call, that throw surfaces as an unhandled promise rejection —
 * which no React error boundary can catch. So the plot has to be guarded before
 * <Canvas> ever mounts, rather than caught after the fact.
 */

let cached: boolean | undefined;

export function hasWebGLSupport(): boolean {
  cached ??= probe();
  return cached;
}

function probe(): boolean {
  try {
    const canvas = document.createElement("canvas");
    // Mirror what the renderer itself asks for: three tries webgl2 first and
    // falls back to webgl, and the plot requests a low-power adapter.
    const gl =
      canvas.getContext("webgl2", { powerPreference: "low-power" }) ??
      canvas.getContext("webgl", { powerPreference: "low-power" });
    if (!gl) return false;
    // Browsers cap how many live contexts one document may hold, so hand this
    // one back immediately instead of waiting for GC — the real canvas is about
    // to ask for one, and on a tight cap the probe could be what denies it.
    gl.getExtension("WEBGL_lose_context")?.loseContext();
    return true;
  } catch {
    // Some browsers throw from getContext instead of returning null.
    return false;
  }
}

/** Test seam: drop the memoized probe result so the next call re-probes. */
export function resetWebGLSupportCache(): void {
  cached = undefined;
}
